import { cnpj as cnpjValidator } from 'cpf-cnpj-validator'
import { useDataActions } from 'hooks'
import { ESTABLISHMENTS } from 'navigation/CONSTANTS'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { setCurrentEstablishment } from 'reduxStore/slices/Establishment'
import { useAppSelector, useAppDispatch } from 'reduxStore/store'
import { useToast } from 'services/context/toast'
import {
  HEALTHCARE_FILTERED,
  HEALTHCARE_REGISTRATION
} from 'services/establishment/CONSTANTS'
import {
  patchEstablishmentLogo,
  removeEstablishmentLogo
} from 'services/establishment/establishmentService'
import { IPostEstablishment } from 'types'
import { searchAddressByCEP } from 'utils/address'
import { resizeImage } from 'utils/image'
import { validateEmail } from 'utils/masks'

const getToastMessage = (isEdition: boolean = false) => {
  if (isEdition) {
    return {
      success: 'Estabelecimento editado com sucesso',
      error: ['Houve um erro ao tentar editar o estabelecimento']
    }
  }

  return {
    success: 'Estabelecimento adicionado com sucesso',
    error: [
      'Houve um erro ao tentar adicionar o estabelecimento',
      'Estabelecimento já existe'
    ]
  }
}

const fieldsNotNull: Array<keyof IPostEstablishment> = [
  'establishment_name',
  'commercial_name',
  'cnpj',
  'cnes',
  'postal_code',
  'number',
  'phone',
  'email'
]

export const useEstablishmentAction = () => {
  const [addressSate, setAddressState] = useState<IStateStdTypes>('idle')
  const [postalCode, setPostalCode] = useState<string>('')
  const { establishmentId } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [first, setFirst] = useState<boolean>(true)
  const currentEstablishment = useAppSelector(state => state.establishment.data)
  const dispatch = useAppDispatch()
  const {
    sendRequest,
    state,
    errorsMap,
    setState,
    setErrorsMap,
    resetInputError,
    setData,
    data,
    ...hooks
  } = useDataActions<IPostEstablishment>({
    patchApi: () => HEALTHCARE_FILTERED(establishmentId),
    postApi: () => HEALTHCARE_REGISTRATION,
    unMaskedKeys: ['phone', 'postal_code', 'cnpj'],
    defaultValue: {
      city: '',
      cnes: '',
      cnpj: '',
      commercial_name: '',
      email: '',
      establishment_name: '',
      establishment_type: 'HOSPITAL',
      number: '',
      phone: '',
      postal_code: '',
      state: '',
      street_address: '',
      complement: '',
      active: true,
      modules_permission: []
    },
    formatData: originData => {
      const newData: IPostEstablishment = {} as IPostEstablishment

      if (hooks.editMode) {
        for (const index in data) {
          const key = index as keyof IPostEstablishment
          let origin: any
          let value: any
          if (['phone', 'cnpj', 'postal_code'].includes(key)) {
            origin = originData[key]?.toString()?.replaceAll(/[^\d]/g, '')
            value = data[key]?.toString()?.replaceAll(/[^\d]/g, '')
          } else {
            origin = originData[key]
            value = data[key]
          }

          // Special handling for modules_permission - include symmetric difference
          if (
            key === 'modules_permission' &&
            Array.isArray(origin) &&
            Array.isArray(value)
          ) {
            const originSet = new Set(origin)
            const valueSet = new Set(value)

            // Calculate symmetric difference (elements that exist in one array but not both)
            const symmetricDiff = [
              ...origin.filter(item => !valueSet.has(item)), // in origin but not in value
              ...value.filter(item => !originSet.has(item)) // in value but not in origin
            ]

            if (symmetricDiff.length > 0) {
              newData[key] = symmetricDiff as never
            }
          } else if (origin != value) {
            newData[key] = (
              typeof value == 'string' ? value?.toString().trim() : value
            ) as never
          }
        }
      } else {
        for (const index in data) {
          const key = index as keyof IPostEstablishment
          const value = data[key]
          const current = typeof value == 'string' ? value.trim() : value
          newData[key] = (
            ['phone', 'cnpj', 'postal_code'].includes(key)
              ? current?.toString()?.replaceAll(/[^\d]/g, '')
              : current === ''
                ? undefined
                : current
          ) as never
        }
      }

      return newData
    },
    fieldsNotNull
  })

  // Logotipo: mantido FORA do `data` do formulário de propósito. O payload de
  // create/update não carrega a imagem — ela vai pelo endpoint dedicado
  // `establishment-logo` (multipart para enviar, JSON com null para remover).
  // Manter aqui evita que o diff do `formatData` envie o campo junto.
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoState, setLogoState] = useState<IStateStdTypes>('idle')

  // `undefined` = usuário ainda não mexeu, então vale o que veio do banco
  // (modo edição). Derivar em vez de sincronizar por efeito evita renders
  // em cascata e mantém o valor correto já no primeiro render.
  const [logoOverride, setLogoOverride] = useState<string | null | undefined>(
    undefined
  )
  const logoPreview =
    logoOverride === undefined ? (data.logo_url ?? null) : logoOverride

  // Revoga o objectURL local para não vazar memória
  useEffect(() => {
    return () => {
      if (logoOverride?.startsWith('blob:')) {
        URL.revokeObjectURL(logoOverride)
      }
    }
  }, [logoOverride])

  const _buildLogoFormData = (file: File): Promise<FormData> =>
    new Promise(resolve => {
      resizeImage(file, 512, 512, blob => {
        const formData = new FormData()
        // Se o redimensionamento falhar, envia o arquivo original
        formData.append('image', blob ?? file, file.name)
        resolve(formData)
      })
    })

  const _uploadLogo = async (establishmentId: string, file: File) => {
    const formData = await _buildLogoFormData(file)
    return patchEstablishmentLogo(establishmentId, formData)
  }

  const handleSelectLogo = async (file: File) => {
    const localPreview = URL.createObjectURL(file)

    // Em cadastro o estabelecimento ainda não existe: guarda em memória e sobe
    // depois do POST. Em edição sobe na hora.
    if (!hooks.editMode || !establishmentId) {
      setLogoFile(file)
      setLogoOverride(localPreview)
      return
    }

    setLogoState('loading')
    const response = await _uploadLogo(establishmentId, file)

    if (response.success) {
      setLogoState('success')
      setLogoOverride(response.data?.url ?? localPreview)
      setLogoFile(null)
    } else {
      setLogoState('error')
      URL.revokeObjectURL(localPreview)
      toast.handler({
        content: 'Houve um erro ao enviar o logotipo, tente novamente.',
        severity: 'error'
      })
    }
  }

  const handleRemoveLogo = async () => {
    // Em cadastro basta descartar o arquivo em memória — nada foi enviado
    if (!hooks.editMode || !establishmentId) {
      setLogoFile(null)
      setLogoOverride(null)
      return
    }

    setLogoState('loading')
    const response = await removeEstablishmentLogo(establishmentId)

    if (response.success) {
      setLogoState('success')
      setLogoFile(null)
      setLogoOverride(null)
    } else {
      setLogoState('error')
      toast.handler({
        content: 'Houve um erro ao remover o logotipo, tente novamente.',
        severity: 'error'
      })
    }
  }

  type ErrorMap = typeof errorsMap

  const validateForm = (): ErrorMap => {
    const newErrorsMap: ErrorMap = new Map()

    const {
      email,
      commercial_name,
      cnpj,
      cnes,
      postal_code,
      establishment_name,
      phone,
      number
    } = data

    if (!cnpjValidator.isValid(cnpj)) {
      newErrorsMap.set('cnpj', 'Digite um CNPJ válido')
    }
    if (establishment_name.length < 3) {
      newErrorsMap.set('establishment_name', 'Digite uma razão social válida')
    }
    if (commercial_name.length < 3) {
      newErrorsMap.set('commercial_name', 'Digite um nome fantasia válido')
    }
    if (!validateEmail(email)) {
      newErrorsMap.set('email', 'Email inválido')
    }
    if (phone.length != 15) {
      newErrorsMap.set('phone', 'Número de telefone inválido')
    }
    if (cnes.length != 7) {
      newErrorsMap.set('cnes', 'Digite um CNES válido')
    }
    if (number.length == 0) {
      newErrorsMap.set('number', 'Digite o número do endereço')
    }

    const postalCodeNumber = postal_code.replaceAll(/[^\d]/g, '')
    if (postalCodeNumber.length != 8) {
      newErrorsMap.set('postal_code', 'Digite um CEP válido')
    }

    errorsMap.clear()

    setErrorsMap(newErrorsMap)

    return newErrorsMap
  }

  const onSubmit = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault()
    event.stopPropagation()

    const errors = validateForm()
    if (state == 'loading' || errors.size > 0) return

    const messages = getToastMessage(hooks.editMode)

    try {
      const result = await sendRequest()

      // Cadastro: o estabelecimento só passa a existir agora, então o logotipo
      // (que ficou em memória) sobe num segundo passo. Uma falha aqui NÃO
      // invalida o cadastro — não há transação entre os dois, e reverter o POST
      // seria pior. O usuário é avisado e pode reenviar pela edição.
      if (!hooks.editMode && logoFile) {
        const newEstablishmentId = result?.establishment_id

        if (newEstablishmentId) {
          const logoResponse = await _uploadLogo(newEstablishmentId, logoFile)

          if (!logoResponse.success) {
            toast.handler({
              content:
                'Estabelecimento criado, mas houve um erro ao enviar o logotipo. Você pode adicioná-lo na edição.',
              severity: 'warning'
            })
          }
        }
      }

      // Check if the current establishment is the one being edited, if yes, update the Redux store with the new modules_permission
      if (
        hooks.editMode &&
        currentEstablishment &&
        establishmentId === currentEstablishment.establishment_id
      ) {
        const updatedEstablishment = {
          ...currentEstablishment,
          modules_permission: data.modules_permission
        }
        dispatch(setCurrentEstablishment(updatedEstablishment))
      }

      setState('success')
      toast.handler({
        content: messages['success']
      })
      navigate(`/${ESTABLISHMENTS}`, {
        replace: true
      })
    } catch (e) {
      setState('error')
      let error: number
      if (e instanceof Error) {
        error = e.message == 'duplicate' ? 1 : 0
      } else {
        error = 0
      }
      toast.handler({
        content: messages['error'][error],
        severity: 'error'
      })
    }
  }

  const _fetchStreetAddress = async () => {
    if (addressSate != 'loading') {
      setAddressState('loading')
      resetInputError('postal_code')

      try {
        const { city, state, street_address } = await searchAddressByCEP(
          data.postal_code
        )

        setData({
          ...data,
          city,
          state,
          street_address
        })
        setAddressState('success')
      } catch (e) {
        const newErrorsMap = new Map(errorsMap)
        newErrorsMap.set('postal_code', 'CEP não encontrado')
        setErrorsMap(newErrorsMap)
        setAddressState('error')
      }
    }
  }

  useEffect(() => {
    if (!first) {
      const { postal_code } = data

      const postalCodeNumber = postal_code.replaceAll(/[^\d]/g, '')
      if (postal_code != postalCode) {
        if (postalCodeNumber.length == 8) {
          _fetchStreetAddress()
        } else {
          setAddressState('idle')
        }

        setPostalCode(postal_code)
      }
    }
    setFirst(false)
  }, [data.postal_code])

  return {
    ...hooks,
    onSubmit,
    errorsMap,
    state,
    disableFields: state == 'loading',
    data,
    showStreetAddress: addressSate == 'success',
    logoPreview,
    logoLoading: logoState == 'loading',
    handleSelectLogo,
    handleRemoveLogo,
    establishmentId
  }
}
